import { sanitize } from 'lettersanitizer';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { EmailAttachment, EmailDetail } from './types';
import { QuotedTextButton } from '~/components/quoted-text-button';
import { Skeleton } from '~/components/ui/skeleton';
import { getPostHog } from '~/lib/posthog';

/**
 * The wrapper id handed to `sanitize()`. The sanitizer prefixes every class it
 * keeps with it, so the class the ingest writes on a quoted chain
 * (`quoted-history`) arrives here as `msg_quoted-history`. Both sides must agree
 * on it.
 */
const EMAIL_SANITIZER_ID = 'msg';
const SANITIZED_QUOTED_REGION_CLASS = `${EMAIL_SANITIZER_ID}_quoted-history`;

type FetchState =
  | { html: string; status: 'ready' }
  | { html: null; status: 'failed' }
  | { html: null; status: 'loading' };

const LOADING_STATE: FetchState = { html: null, status: 'loading' };

/**
 * The body of one message.
 *
 * A small body travels on the row and renders immediately. A large one lives in
 * storage and is fetched lazily by the message actually on screen, rather than
 * upfront for the whole thread — a collapsed conversation must not block on an
 * 8 MB body.
 *
 * **Both branches render through `EmailFrame`, which sanitizes.** That is not
 * belt-and-braces: mail is stored verbatim, scripts and inline `on*=` handlers
 * included, which is only safe because every render path sanitizes. An
 * unsanitized branch here is live XSS.
 */
export function EmailBody({ email }: { email: EmailDetail }) {
  return (
    <EmailBodyContent
      body={email.body}
      subject={email.subject}
      textContent={email.textContent}
      attachments={email.attachments}
    />
  );
}

/**
 * The three-state body, rendered. Split out of `EmailBody` so the contact feed
 * shows the same HTML through the same sanitizing frame without needing a whole
 * `EmailDetail` — and without a second copy of this switch, where a missed branch
 * would be an unsanitized one.
 */
export function EmailBodyContent({
  body,
  subject,
  textContent,
  attachments,
}: {
  body: EmailDetail['body'];
  subject: string;
  textContent: string;
  attachments: Array<EmailAttachment>;
}) {
  if (body.kind === 'none') {
    return <EmailTextBody text={textContent} />;
  }

  if (body.kind === 'inline') {
    return (
      <EmailFrame
        title={subject}
        htmlContent={body.html}
        textContent={textContent.trim()}
        attachments={attachments}
      />
    );
  }

  // No URL for a body we know exists: same rendering failure as a failed fetch.
  if (body.url === null) {
    return <EmailTextBody text={textContent} />;
  }

  return (
    <StoredEmailBody
      attachments={attachments}
      htmlUrl={body.url}
      subject={subject}
      textContent={textContent}
    />
  );
}

export function EmailTextBody({ text }: { text: string }) {
  return (
    <div className="max-w-email whitespace-pre-wrap wrap-anywhere text-sm leading-5 text-foreground">
      {text.trim()}
    </div>
  );
}

function StoredEmailBody({
  attachments,
  htmlUrl,
  subject,
  textContent,
}: {
  attachments: Array<EmailAttachment>;
  htmlUrl: string;
  subject: string;
  textContent: string;
}) {
  const state = useStoredHtml(htmlUrl);

  if (state.status === 'loading') {
    return <Skeleton className="h-40 w-full max-w-email rounded-md" />;
  }

  // The body is in storage, not on the row, so a fetch failure is a rendering
  // problem: fall back to the text part, which is always populated.
  if (state.status === 'failed') {
    return <EmailTextBody text={textContent} />;
  }

  return (
    <EmailFrame
      title={subject}
      htmlContent={state.html}
      textContent={textContent.trim()}
      attachments={attachments}
    />
  );
}

/** The state is tagged with the URL it answers, so a new URL reads as loading
 *  at once instead of showing the previous body until the effect catches up. */
function useStoredHtml(htmlUrl: string): FetchState {
  const [settled, setSettled] = React.useState<{
    state: FetchState;
    url: string;
  } | null>(null);

  React.useEffect(() => {
    const controller = new AbortController();
    const setState = (state: FetchState) => setSettled({ state, url: htmlUrl });

    void fetch(htmlUrl, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Email body request failed: ${response.status}`);
        }

        return await response.text();
      })
      .then((html) => setState({ html, status: 'ready' }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }

        getPostHog()?.captureException(error);
        setState({ html: null, status: 'failed' });
      });

    return () => controller.abort();
  }, [htmlUrl]);

  return settled?.url === htmlUrl ? settled.state : LOADING_STATE;
}

/**
 * Exported for the contact feed, which shows the same HTML in a smaller bubble.
 *
 * It is the *only* thing either surface may render a body through. Mail is
 * stored as it arrived, sanitized by nothing on the way in, so this is the path
 * that makes it safe: `sanitize` from lettersanitizer, then a `sandbox` without
 * `allow-scripts` under a `default-src 'none'` CSP.
 */
export function EmailFrame({
  title,
  htmlContent,
  textContent,
  attachments,
}: {
  title: string;
  htmlContent: string;
  textContent: string;
  attachments: Array<EmailAttachment>;
}) {
  const { t } = useTranslation('mailbox');
  const frameTitle = title || t('Email content');
  // Viewing state and nothing else: navigating away re-collapses the chain.
  const [showQuotedRegion, setShowQuotedRegion] = React.useState(false);
  const sanitizedHtml = React.useMemo(
    () => sanitizeEmailHtml(htmlContent, textContent, attachments),
    [attachments, htmlContent, textContent],
  );
  const hasQuotedRegion = sanitizedHtml.includes(SANITIZED_QUOTED_REGION_CLASS);
  const cleanupResizeObserverRef = React.useRef<() => void>(() => undefined);

  React.useEffect(() => () => cleanupResizeObserverRef.current(), []);

  function handleLoad(event: React.SyntheticEvent<HTMLIFrameElement>) {
    cleanupResizeObserverRef.current();
    cleanupResizeObserverRef.current = observeEmailFrameHeight(
      event.currentTarget,
    );
  }

  return (
    <div className="flex w-full max-w-email flex-col items-start gap-2">
      {/* Starts flat rather than at a guessed height: `onLoad` gives it the
          mail's own, and any taller start is blank space until it does. */}
      <iframe
        title={frameTitle}
        // `<base target="_blank">` sends every link to a new tab, which a
        // sandbox refuses without `allow-popups`; escaping the sandbox lets the
        // page it opens work normally. Still no `allow-scripts`.
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        scrolling="no"
        srcDoc={buildEmailFrameDocument(
          sanitizedHtml,
          attachments,
          showQuotedRegion,
        )}
        onLoad={handleLoad}
        className="h-0 w-full rounded-md bg-white"
      />
      {/* The frame has no `allow-scripts`, so the toggle cannot live inside the
          body it collapses — it hides the marked region through the stylesheet
          we already build for the frame. */}
      {hasQuotedRegion && !showQuotedRegion && (
        <QuotedTextButton onShow={() => setShowQuotedRegion(true)} />
      )}
    </div>
  );
}

function observeEmailFrameHeight(iframe: HTMLIFrameElement) {
  const body = iframe.contentDocument?.body;

  if (!body) {
    return () => undefined;
  }

  resizeEmailFrame(iframe);

  if (typeof ResizeObserver === 'undefined') {
    return () => undefined;
  }

  let animationFrameId = 0;
  const scheduleResize = () => {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
    }

    animationFrameId = requestAnimationFrame(() => {
      animationFrameId = 0;
      resizeEmailFrame(iframe);
    });
  };
  const resizeObserver = new ResizeObserver(scheduleResize);

  // Only `body`: the root element resizes with the frame we are sizing, so
  // watching it would be watching our own output.
  resizeObserver.observe(body);
  scheduleResize();

  return () => {
    resizeObserver.disconnect();

    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
    }
  };
}

/**
 * The frame is exactly as tall as the mail inside it.
 *
 * Measured on `body` alone, deliberately. The root element's scroll height is
 * never less than the viewport — which is the frame we are sizing — so
 * measuring it means the frame can only ever grow, and a two-line message keeps
 * whatever height the frame happened to start at. `body` has no such floor: its
 * height is its content, and the stylesheet above gives it `overflow: hidden`
 * so nothing escapes to be missed.
 */
function resizeEmailFrame(iframe: HTMLIFrameElement) {
  const body = iframe.contentDocument?.body;

  if (!body) {
    return;
  }

  const contentHeight = Math.ceil(
    Math.max(body.scrollHeight, body.getBoundingClientRect().height),
  );

  if (contentHeight > 0) {
    iframe.style.height = `${contentHeight}px`;
  }
}

function sanitizeEmailHtml(
  htmlContent: string,
  textContent: string,
  attachments: Array<EmailAttachment>,
) {
  if (typeof DOMParser === 'undefined') {
    return '';
  }

  const cidUrls = new Map(
    attachments
      .filter((attachment): attachment is EmailAttachment & { url: string } =>
        Boolean(attachment.contentId && attachment.url),
      )
      .map((attachment) => [
        cidKey(attachment.contentId ?? ''),
        attachment.url,
      ]),
  );

  // A fixed id, so the class the ingest wrote on the quoted region has a name
  // the stylesheet below can target. Left to itself the sanitizer invents 24
  // random characters per render.
  return sanitize(htmlContent, textContent, {
    id: EMAIL_SANITIZER_ID,
    rewriteExternalResources: (url) => rewriteEmailResourceUrl(url, cidUrls),
  });
}

/**
 * The single choke point for every remote URL that can enter a rendered body.
 *
 * Remote images load on sight, so a sender learns their mail was opened — that
 * is the deliberate trade for messages that render as they were written. It
 * stays a choke point because a proxy would be a change here and nowhere else.
 * Anything that is not `cid:`, `data:image/` or http(s) is still blanked.
 */
function rewriteEmailResourceUrl(url: string, cidUrls: Map<string, string>) {
  const resourceUrl = url.trim();
  const lowerUrl = resourceUrl.toLowerCase();

  if (lowerUrl.startsWith('cid:')) {
    return cidUrls.get(cidKey(resourceUrl)) ?? '';
  }

  if (lowerUrl.startsWith('data:image/')) {
    return resourceUrl;
  }

  if (isRemoteUrl(resourceUrl)) {
    return resourceUrl;
  }

  return '';
}

function isRemoteUrl(url: string) {
  const lowerUrl = url.trim().toLowerCase();

  return lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://');
}

function cidKey(value: string) {
  return value.trim().replace(/^cid:/i, '').replace(/^<|>$/g, '');
}

function buildEmailFrameDocument(
  htmlContent: string,
  attachments: Array<EmailAttachment>,
  showQuotedRegion: boolean,
) {
  const trustedImageOrigins = [
    ...new Set(
      attachments
        .filter((attachment) => Boolean(attachment.contentId && attachment.url))
        .flatMap((attachment) => {
          try {
            return [new URL(attachment.url ?? '').origin];
          } catch {
            return [];
          }
        }),
    ),
  ];
  const imageSources = [
    'data:',
    ...trustedImageOrigins,
    'https:',
    'http:',
  ].join(' ');
  const contentSecurityPolicy = [
    "default-src 'none'",
    `img-src ${imageSources}`,
    "style-src 'unsafe-inline'",
    "base-uri 'none'",
    "form-action 'none'",
    "frame-src 'none'",
    "connect-src 'none'",
    "font-src 'none'",
    "media-src 'none'",
  ].join('; ');
  // The frame is deliberately denied `allow-scripts`, so collapsing the quoted
  // chain has to happen here: the region was marked at ingest and this is the
  // stylesheet that hides it.
  const quotedRegionRule = showQuotedRegion
    ? ''
    : `.${SANITIZED_QUOTED_REGION_CLASS} { display: none !important; }`;

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta http-equiv="Content-Security-Policy" content="${contentSecurityPolicy}" />
    <base target="_blank" />
    <style>
      html,
      body {
        margin: 0;
        background: white;
        color: #18181b;
        font: 14px/1.5 system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      }

      body {
        padding: 16px;
        overflow: hidden;
        overflow-wrap: anywhere;
      }

      img,
      video,
      table {
        max-width: 100%;
      }

      pre {
        white-space: pre-wrap;
      }

      ${quotedRegionRule}
    </style>
  </head>
  <body>
    ${htmlContent}
  </body>
</html>`;
}
